import {
  CodatLogoBlueSquare,
  ErrorDialog,
  OrchardProvider,
  Skeleton,
  Typography,
} from "@codat/orchard-ui";
import { UserClientInfo, useUserClientsQuery } from "api/clients";
import { GetUserValueFromToken } from "api/setup";
import { CodatError } from "api/users";
import React from "react";
import { useTitle } from "utils/useTitle";

import LogOutButton from "components/LogOutButton";

import ClientCard from "./ClientCard";
import styles from "./ClientSelect.module.css";

interface ClientSelectPageContentProps {
  clients: UserClientInfo[];
  email: string | null;
}

const ClientSelectPageContent: React.FC<ClientSelectPageContentProps> = ({
  clients,
  email,
}) => {
  return (
    <div data-testid="client-select" className={styles.clientSelectPage}>
      <div className={styles.logOutButtonWrapper}>
        <LogOutButton />
      </div>
      <div className={styles.header}>
        <CodatLogoBlueSquare className={styles.logo} />
        <Typography variant="heading_xxl" as="h1">
          Select a client to launch
        </Typography>
        <p className={styles.headerSubtext}>
          You are logged in as <span className={styles.email}>{email}</span>
        </p>
      </div>

      <ul
        data-testid="client-select-containers"
        className={styles.clientCardContainer}
      >
        {clients.map((client) => (
          <li key={client.clientId}>
            <ClientCard key={client.clientId} client={client} />
          </li>
        ))}
      </ul>
    </div>
  );
};

const LoadingSkeleton = () => (
  <div className={styles.clientSelectPage}>
    <div className={styles.logOutButtonWrapper}>
      <LogOutButton />
    </div>
    <div className={styles.loadingSkeleton}>
      <div className={styles.header}>
        <Skeleton width="3.5rem" height="3.5rem" />
        <Skeleton
          className={styles.skeletonMainHeading}
          width="20rem"
          height="2.25rem"
        />
        <Skeleton className={styles.skeletonSubHeading} />
        <div className={styles.skeletonCardWrapper}>
          <Skeleton className={styles.skeletonCard} />
          <Skeleton className={styles.skeletonCard} />
          <Skeleton className={styles.skeletonCard} />
        </div>
      </div>
    </div>
  </div>
);

export const ClientSelect: React.FC = () => {
  useTitle("Select client");
  const [{ loading, error, data: clients }] = useUserClientsQuery();

  const userEmail = GetUserValueFromToken("user_email");
  const loadError = error;
  const codatError = loadError?.response?.data as CodatError;

  // 401 error will redirect to sign in page, so keep skeleton while redirecting
  if (loading || (codatError && codatError.statusCode < 500)) {
    return <LoadingSkeleton />;
  }

  if (loadError || !clients) {
    const errorMessage = codatError?.error
      ? `An error occured getting users: ${codatError?.error}`
      : "An unexpected error occured getting users";

    return (
      <ErrorDialog
        statusCode={codatError?.statusCode ?? 500}
        correlationId={codatError?.correlationId}
        message={errorMessage}
      />
    );
  }

  return <ClientSelectPageContent clients={clients} email={userEmail} />;
};

export const ClientSelectRemote = () => (
  <OrchardProvider appId="client-select">
    <ClientSelect />
  </OrchardProvider>
);
